import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login/Login';
import SolarDashboard from './components/Dashboard/SolarDashboard';
import UserPowerLocationStatus from './components/UserPowerLocationStatus/UserPowerLocationStatus';
import PowerPlantDetailPage from './components/PowerPlantDetail/PowerPlantDetailPage';
import RegistrationPage from './components/Registration/RegistrationPage';
import PowerPlantInquiry from './components/Registration/PowerPlantInquiry';
import Notification from './components/board/notification/Notification';
import ProductPurchasePage from './components/Product/ProductPurchasePage';
import MyProfilePage from './components/User/MyProfilePage';
import PaymentHistoryPage from './components/User/PaymentHistoryPage';
import PolicyPage from './components/Policy/PolicyPage';
import Unauthorized from './components/Unauthorized';
import ProtectedRoute from './components/ProtectedRoute';
import CommonLayout from './components/Common/CommonLayout';
import AlarmModal from './components/board/alarm/AlarmModal';
import { authAPI } from './services/api';
import { SubscriptionProvider, useSubscription } from './context/SubscriptionContext';
import useAutoRefresh from './hooks/useAutoRefresh';
import useAlarmNotification from './hooks/useAlarmNotification';
import './App.css';

function AppRoutes({
  user,
  userRole,
  isInitializing,
  isLoggedIn,
  handleLogin,
  handleLogout,
}) {
  const { activeSubscriptionCount } = useSubscription();
  const { newAlarm, closeModal, markAlarmAsRead } = useAlarmNotification(
    user && activeSubscriptionCount > 0 ? user : null,
    30000
  );

  return (
    <>
      <div className="App">
        <Routes>
          <Route
            path="/login"
            element={
              isLoggedIn ?
                <Navigate to="/" replace /> :
                <CommonLayout>
                  <Login onLogin={handleLogin} />
                </CommonLayout>
            }
          />

          <Route
            path="/"
            element={
              <ProtectedRoute userRole={userRole} isInitializing={isInitializing}>
                <CommonLayout user={user} onLogout={handleLogout} showTabs={false}>
                  <UserPowerLocationStatus user={user} onLogout={handleLogout} />
                </CommonLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/power-plant/:id"
            element={
              <ProtectedRoute userRole={userRole} isInitializing={isInitializing}>
                <PowerPlantDetailPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute userRole={userRole} requiredRole="ADMIN" isInitializing={isInitializing}>
                <CommonLayout user={user} onLogout={handleLogout}>
                  <SolarDashboard user={user} onLogout={handleLogout} />
                </CommonLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/create"
            element={
              <ProtectedRoute userRole={userRole} requiredRole="ADMIN" isInitializing={isInitializing}>
                <CommonLayout user={user} onLogout={handleLogout}>
                  <RegistrationPage user={user} onLogout={handleLogout} />
                </CommonLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/inquiry"
            element={
              <ProtectedRoute userRole={userRole} requiredRole="ADMIN" isInitializing={isInitializing}>
                <CommonLayout user={user} onLogout={handleLogout}>
                  <PowerPlantInquiry />
                </CommonLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/notice"
            element={
              <ProtectedRoute userRole={userRole} isInitializing={isInitializing}>
                <CommonLayout user={user} onLogout={handleLogout} showTabs={false}>
                  <Notification user={user} />
                </CommonLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/products"
            element={
              <ProtectedRoute userRole={userRole} isInitializing={isInitializing}>
                <CommonLayout user={user} onLogout={handleLogout} showTabs={false}>
                  <ProductPurchasePage user={user} />
                </CommonLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute userRole={userRole} isInitializing={isInitializing}>
                <CommonLayout user={user} onLogout={handleLogout} showTabs={false}>
                  <MyProfilePage user={user} />
                </CommonLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/payment-history"
            element={
              <ProtectedRoute userRole={userRole} isInitializing={isInitializing}>
                <CommonLayout user={user} onLogout={handleLogout} showTabs={false}>
                  <PaymentHistoryPage user={user} />
                </CommonLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/policies/:policySlug"
            element={
              <CommonLayout>
                <PolicyPage />
              </CommonLayout>
            }
          />

          <Route
            path="/unauthorized"
            element={
              <CommonLayout>
                <Unauthorized />
              </CommonLayout>
            }
          />
        </Routes>
      </div>

      {newAlarm && (
        <AlarmModal
          alarm={newAlarm}
          onClose={closeModal}
          onMarkAsRead={markAlarmAsRead}
        />
      )}
    </>
  );
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');

    if (token && userData) {
      authAPI.getCurrentUser()
        .then((currentUserToken) => {
          localStorage.setItem('authToken', currentUserToken);
          const parsedUserData = JSON.parse(userData);
          setUser(parsedUserData);
          setUserRole(parsedUserData.role || 'USER');
          setIsLoggedIn(true);
          setIsInitializing(false);
        })
        .catch(() => {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          setIsInitializing(false);
        });
    } else {
      setIsInitializing(false);
    }
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    setUserRole(userData.role || 'USER');
    setIsLoggedIn(true);
    setIsInitializing(false);
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      setUser(null);
      setUserRole(null);
      setIsLoggedIn(false);
      setIsInitializing(false);
    }
  };

  useAutoRefresh(isLoggedIn, handleLogout);

  return (
    <Router>
      <SubscriptionProvider user={user}>
        <AppRoutes
          user={user}
          userRole={userRole}
          isInitializing={isInitializing}
          isLoggedIn={isLoggedIn}
          handleLogin={handleLogin}
          handleLogout={handleLogout}
        />
      </SubscriptionProvider>
    </Router>
  );
}

export default App;
